import { ArrowRight, CheckCircle2, Link2, Pencil, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { CatalogModuleView, ProjectSummary } from '../../types'
import { MODULE_CATEGORY_ICON, MODULE_CATEGORY_TONE } from '../../visuals'
import { ProjectIcon } from '../ProjectBits'
import { RowMenu } from '../RowMenu'
import type { IntegrateTarget } from './IntegrateModal'
import { usePriceFormat } from '../../usePriceFormat'

type Props = {
  module: CatalogModuleView
  nameOf: Map<string, string>
  projects: ProjectSummary[]
  canWrite: boolean
  onEdit: () => void
  onDelete: () => void
  onIntegrate: (target: IntegrateTarget) => void
}

export function ModuleCard({ module: m, nameOf, projects, canWrite, onEdit, onDelete, onIntegrate }: Props) {
  const { t } = useTranslation()
  const fmtPrice = usePriceFormat()
  const Icon = MODULE_CATEGORY_ICON[m.category]
  const projectById = new Map(projects.map((p) => [p.id, p]))
  const free = projects.filter((p) => !m.usage.some((u) => u.projectId === p.id))

  return (
    <article className={cn('flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-card-hover motion-reduce:hover:translate-y-0', !m.active && 'opacity-75')}>
      <header className="flex items-start gap-3">
        <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg', MODULE_CATEGORY_TONE[m.category])}>
          <Icon className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-semibold">
            <Link to={`/projects/catalog/${m.id}`} className="hover:text-primary focus-visible:outline-none focus-visible:underline">
              {m.name}
            </Link>
          </h3>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-[11px] text-muted-foreground">{m.code}</span>
            <Badge tone="outline" className="rounded-md py-0 text-[11px]">{t(`projects.moduleCategory.${m.category}`)}</Badge>
            {!m.active ? <Badge tone="neutral" className="rounded-md py-0 text-[11px]">{t('projects.catalog.passive')}</Badge> : null}
          </div>
        </div>
        {canWrite ? <RowMenu label={m.name} onEdit={onEdit} onDelete={onDelete} /> : null}
      </header>

      {m.description ? <p className="line-clamp-2 text-sm text-muted-foreground">{m.description}</p> : null}

      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-lg font-semibold tabular-nums">
          {fmtPrice(m.price)}
          {m.pricing !== 'one_time' ? <span className="text-xs font-normal text-muted-foreground"> {t(`projects.pricingSuffix.${m.pricing}`)}</span> : null}
        </span>
        <span className="text-xs text-muted-foreground">{t(`projects.pricing.${m.pricing}`)}</span>
      </div>
      {m.features.length ? (
        <ul className="space-y-1">
          {m.features.slice(0, 3).map((f) => (
            <li key={f.id} className="flex items-center gap-1.5 text-xs">
              <CheckCircle2 className="size-3.5 shrink-0 text-success" aria-hidden />
              <span className="truncate">{f.name}</span>
            </li>
          ))}
          {m.features.length > 3 ? <li className="pl-5 text-xs text-muted-foreground">{t('projects.catalog.moreFeatures', { count: m.features.length - 3 })}</li> : null}
        </ul>
      ) : null}
      {m.dependsOn.length ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Link2 className="size-3.5 shrink-0" aria-hidden />
          <span className="truncate">{t('projects.modules.requiresInline', { names: m.dependsOn.map((id) => nameOf.get(id) ?? '?').join(', ') })}</span>
        </p>
      ) : null}

      <div className="mt-auto space-y-2 border-t pt-3">
        <p className="text-xs font-medium text-muted-foreground">{t('projects.catalog.usedIn', { count: m.usage.length })}</p>
        {m.usage.length === 0 ? <p className="text-xs text-muted-foreground">{t('projects.catalog.notUsed')}</p> : null}
        <ul className="flex flex-wrap gap-1.5">
          {m.usage.map((u) => {
            const p = projectById.get(u.projectId)
            if (!p) return null
            const chip = (
              <>
                <ProjectIcon icon={p.icon} color={p.color} className="size-5 rounded-md shadow-none" />
                <span className="truncate">{p.name}</span>
                <span className={cn('size-1.5 shrink-0 rounded-full', u.requirement === 'required' ? 'bg-success' : 'bg-info')} aria-label={t(`projects.requirement.${u.requirement}`)} />
              </>
            )
            return (
              <li key={u.projectId} className="max-w-full">
                {canWrite ? (
                  <button
                    type="button"
                    onClick={() => onIntegrate({ kind: 'edit', moduleId: m.id, projectId: u.projectId })}
                    className="group flex max-w-full items-center gap-1.5 rounded-full border bg-card py-0.5 pl-0.5 pr-2 text-xs font-medium transition-colors hover:border-primary/40 hover:bg-primary/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                    title={t('projects.catalog.chipHint', { requirement: t(`projects.requirement.${u.requirement}`), price: fmtPrice(u.price) })}
                    aria-label={t('projects.catalog.pricing.editLinkFor', { module: m.name, project: p.name })}
                  >
                    {chip}
                    <Pencil className="size-3 shrink-0 text-muted-foreground group-hover:text-primary" aria-hidden />
                  </button>
                ) : (
                  <span className="flex max-w-full items-center gap-1.5 rounded-full border bg-card py-0.5 pl-0.5 pr-2.5 text-xs font-medium" title={t('projects.catalog.chipHint', { requirement: t(`projects.requirement.${u.requirement}`), price: fmtPrice(u.price) })}>
                    {chip}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
        <div className="flex gap-2 pt-1">
          <Button size="sm" variant="outline" className="flex-1" asChild>
            <Link to={`/projects/catalog/${m.id}`}>
              {t('projects.catalog.details')}
              <ArrowRight />
            </Link>
          </Button>
          {canWrite && m.active && free.length ? (
            <Button size="sm" variant="outline" className="flex-1" onClick={() => onIntegrate({ kind: 'create', moduleId: m.id })}>
              <Plus />
              {t('projects.catalog.addToProject')}
            </Button>
          ) : null}
        </div>
      </div>
    </article>
  )
}
